import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Shared Goal Fit Scorer — mirrors backend logic exactly.
 * Weights: protein 40%, calories 30%, carbs 15%, fat 15%
 */
function macroFit(foodVal, remainingVal) {
  if (remainingVal <= 0) return foodVal > 0 ? Math.max(0, 100 - foodVal * 2) : 100;
  const ratio = foodVal / remainingVal;
  if (ratio <= 1.0) return ratio * 100;
  const overshoot = ratio - 1.0;
  return Math.max(0, 100 - overshoot * 80);
}

export function scoreGoalFit(remaining, food, mode = 'progress', mealsLogged = 1) {
  if (mealsLogged === 0) {
    return { fitScore: 75, fitLevel: 'good', message: 'No meals logged yet today — this would be a great start!', detail: {} };
  }

  const remCal = Math.max(remaining.calories || 0, 1);
  const remProt = Math.max(remaining.protein || 0, 1);
  const remCarbs = Math.max(remaining.carbs || 0, 1);
  const remFat = Math.max(remaining.fat || 0, 1);

  const fCal = food.calories || 0;
  const fProt = food.protein || 0;
  const fCarbs = food.carbs || 0;
  const fFat = food.fat || 0;

  const protFit = macroFit(fProt, remProt);
  const calFit = macroFit(fCal, remCal);
  const carbFit = macroFit(fCarbs, remCarbs);
  const fatFit = macroFit(fFat, remFat);

  let score = Math.round(protFit * 0.40 + calFit * 0.30 + carbFit * 0.15 + fatFit * 0.15);
  score = Math.max(0, Math.min(100, score));

  const fits = { protein: protFit, calories: calFit, carbs: carbFit, fat: fatFit };
  const weakest = Object.entries(fits).sort((a, b) => a[1] - b[1])[0][0];

  const protPct = Math.round(Math.min((fProt / remProt) * 100, 999));
  const calPct = Math.round(Math.min((fCal / remCal) * 100, 999));

  let level, message;
  if (score >= 80) {
    level = 'great';
    message = `Great choice — covers ${protPct}% of your remaining protein and fits your calorie budget.`;
  } else if (score >= 60) {
    level = 'good';
    message = `This works — slightly over on ${weakest} but protein and calories are on track.`;
  } else {
    level = 'poor';
    message = mode === 'gentle'
      ? "This is a delicious recipe. It's on the bigger side for today, but there's no wrong choice."
      : `Heads up — ${Math.round(fCal)} cal and you have ${Math.round(remCal)} left. Great for a day with more room.`;
  }

  return { fitScore: score, fitLevel: level, message, detail: { proteinPct: protPct, calPct, weakest: score < 80 ? weakest : null } };
}

/**
 * React hook — computes fit for a food item against user's remaining targets.
 */
export function useGoalFit(food, dailyTotals, targets, mealsLogged) {
  const { user } = useAuth();
  const mode = user?.experience_mode || 'progress';

  return useMemo(() => {
    if (!food || !targets) return null;
    const remaining = {
      calories: (targets.calories || 0) - (dailyTotals?.calories || 0),
      protein: (targets.protein || 0) - (dailyTotals?.protein || 0),
      carbs: (targets.carbs || 0) - (dailyTotals?.carbs || 0),
      fat: (targets.fat || 0) - (dailyTotals?.fat || 0),
    };
    return scoreGoalFit(remaining, food, mode, mealsLogged ?? 1);
  }, [food, dailyTotals, targets, mode, mealsLogged]);
}

/**
 * FitTag component — renders "Great fit" / "Good fit" badge inline.
 */
export function FitTag({ fitLevel, fitScore, compact = false }) {
  if (!fitLevel || fitLevel === 'poor') return null;
  const isGreat = fitLevel === 'great';
  return (
    <span
      data-testid="fit-tag"
      style={{
        display: 'inline-block',
        padding: compact ? '2px 6px' : '3px 8px',
        borderRadius: 6,
        fontSize: compact ? 9 : 10,
        fontWeight: 600,
        fontFamily: 'var(--font-sans)',
        background: isGreat ? '#5A8C6A15' : '#E5A84B15',
        color: isGreat ? '#5A8C6A' : '#E5A84B',
      }}
    >
      {isGreat ? 'Great fit' : 'Good fit'}
    </span>
  );
}

/**
 * FitContext — one-line context string for recipe cards.
 */
export function fitContextLine(fitResult, remaining) {
  if (!fitResult) return '';
  if (fitResult.fitLevel === 'great') {
    return `Fills ${fitResult.detail.proteinPct}% of your remaining protein`;
  }
  if (fitResult.fitLevel === 'good') {
    return `${Math.round(remaining?.calories || 0)} cal remaining today`;
  }
  return '';
}
